import { Button as MuiButton, ButtonProps as MuiButtonProps, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

import CancelIcon from '@icons/cancel-icon.svg';
import CloseEyeIcon from '@icons/close-eye-icon.svg';
import DownloadIcon from '@icons/download-icon.svg';
import ExpandIcon from '@icons/expand-icon.svg';
import ImprovePromptIcon from '@icons/improve-prompt-icon.svg';
import OpenEyeIcon from '@icons/open-eye-icon.svg';
import ReloadIcon from '@icons/reload-icon.svg';
import SendIcon from '@icons/send-icon.svg';
import SuggestionIcon from '@icons/suggestion-icon.svg';
import WhiteBulbIcon from '@icons/white-bulb-icon.svg';

type IconType =
    | 'download'
    | 'expand'
    | 'improve'
    | 'send'
    | 'suggestion'
    | 'cancel'
    | 'reload'
    | 'close-eye'
    | 'open-eye'
    | 'white-bulb';
type ButtonBackgroundColor = 'lightPurple' | 'purple' | 'orange' | 'black';
type ButtonBackgroundGradient = 'primaryLinearGradient' | 'secondaryLinearGradient';
type BackgroundVariant = ButtonBackgroundColor | ButtonBackgroundGradient;
type BoxShadow = 'primary' | 'secondary' | 'primaryGradient' | 'secondaryGradient';
type BorderColor = 'white';

enum ButtonVariant {
    CONTAINED = 'contained',
    OUTLINED = 'outlined',
    TEXT = 'text',
}

interface CustomButtonProps extends MuiButtonProps {
    iconType?: IconType;
    alternateStyle?: boolean;
    lighterFont?: boolean;
    colorVariant?: BackgroundVariant;
    whiteText?: boolean;
    boxShadow?: BoxShadow;
    borderColor?: BorderColor;
    minWidth?: number;
}

const iconMap: Record<IconType, string> = {
    download: DownloadIcon,
    expand: ExpandIcon,
    improve: ImprovePromptIcon,
    send: SendIcon,
    suggestion: SuggestionIcon,
    cancel: CancelIcon,
    reload: ReloadIcon,
    'close-eye': CloseEyeIcon,
    'open-eye': OpenEyeIcon,
    'white-bulb': WhiteBulbIcon,
};

const Button = ({
    iconType,
    children,
    alternateStyle,
    lighterFont,
    colorVariant,
    whiteText,
    variant = ButtonVariant.CONTAINED,
    boxShadow,
    borderColor,
    minWidth,
    ...rest
}: CustomButtonProps) => {
    const getStyles = (variant: ButtonVariant, theme: Theme): SystemStyleObject<Theme> => {
        const {
            palette: { customColors },
            borderRadius: { semiLarge },
            gradients,
            borderWidth,
            customShadows,
        } = theme;

        const backgroundOptions = {
            ...customColors,
            ...gradients,
        };

        const background = colorVariant
            ? backgroundOptions[colorVariant]
            : customColors.lightPurple;

        const borderTopLeftRadius = alternateStyle ? 0 : semiLarge;
        const borderTopRightRadius = semiLarge;
        const borderBottomRightRadius = alternateStyle ? semiLarge : 0;
        const borderBottomLeftRadius = semiLarge;

        switch (variant) {
            case ButtonVariant.CONTAINED:
                return {
                    background,
                    color: whiteText ? customColors.white : customColors.black,
                    borderTopLeftRadius,
                    borderTopRightRadius,
                    borderBottomRightRadius,
                    borderBottomLeftRadius,
                    boxShadow: boxShadow ? customShadows[boxShadow] : customShadows.primary,
                    border: borderColor
                        ? `${borderWidth.thin} solid ${customColors[borderColor]}`
                        : 'none',
                    '&.Mui-disabled': {
                        opacity: 0.5,
                        color: whiteText ? customColors.white : customColors.black,
                    },
                };
            case ButtonVariant.OUTLINED:
                return {
                    position: 'relative',
                    background,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    border: 'none',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        padding: borderWidth.thin,
                        background: background,
                        borderTopLeftRadius,
                        borderTopRightRadius,
                        borderBottomRightRadius,
                        borderBottomLeftRadius,
                        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        maskComposite: 'xor',
                        WebkitMask:
                            'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        pointerEvents: 'none',
                    },
                };
            case ButtonVariant.TEXT:
            default:
                return {};
        }
    };

    const iconSrc = iconType ? iconMap[iconType] : null;

    return (
        <MuiButton
            {...rest}
            variant={variant}
            startIcon={iconSrc && <img src={iconSrc} alt='' aria-hidden='true' />}
            sx={(theme) => ({
                fontSize: '0.875rem',
                textTransform: 'none',
                fontWeight: lighterFont ? 600 : 700,
                minHeight: '36px',
                minWidth: minWidth ? minWidth : 'fit-content',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                ...getStyles(variant as ButtonVariant, theme),
            })}
        >
            {children}
        </MuiButton>
    );
};

export default Button;
